<script>
import axios from "axios";

const clone = (value) => JSON.parse(JSON.stringify(value ?? null));
const emptyContact = () => ({
  name: "",
  relationship: "",
  phone: "",
  address: "",
});
const emptySchool = () => ({ name: "", startDate: "", endDate: "" });

export default {
  name: "ApplicationEditModal",
  props: {
    application: { type: Object, required: true },
  },
  emits: ["close", "saved"],
  data() {
    return {
      saving: false,
      error: "",
      reason: "",
      activeTab: "personal",
      countries: [],
      states: [],
      lgas: [],
      relationshipOptions: [
        "Parent",
        "Father",
        "Mother",
        "Sibling",
        "Spouse",
        "Uncle",
        "Aunt",
        "Cousin",
        "Guardian",
        "Other",
      ],
      examTypes: ["WAEC", "NECO", "NABTEB", "GCE"],
      subjectOptions: [
        "Mathematics",
        "English",
        "Biology",
        "Chemistry",
        "Physics",
        "Government",
        "Economics",
        "Commerce",
        "Literature",
        "Geography",
        "Further Mathematics",
        "Agricultural Science",
      ],
      grades: ["A1", "B2", "B3", "C4", "C5", "C6", "D7", "E8", "F9"],
      form: {},
    };
  },
  created() {
    this.hydrate();
    this.fetchCountries();
    this.fetchStates();
  },
  methods: {
    dateValue(value) {
      if (!value) return "";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? ""
        : date.toISOString().slice(0, 10);
    },
    hydrate() {
      const application = this.application;
      const academic = clone(application.academicBackground) || {};
      this.form = {
        gender: application.gender || "",
        nationality: application.nationality || "",
        stateOfOrigin: application.stateOfOrigin || "",
        lga: application.lga || "",
        placeOfBirth: application.placeOfBirth || "",
        maritalStatus: application.maritalStatus || "",
        permanentAddress: {
          line1: application.permanentAddress?.line1 || "",
          line2: application.permanentAddress?.line2 || "",
          city: application.permanentAddress?.city || "",
          state: application.permanentAddress?.state || "",
          country: application.permanentAddress?.country || "",
        },
        guardian: { ...emptyContact(), ...(clone(application.guardian) || {}) },
        nextOfKin: {
          ...emptyContact(),
          ...(clone(application.nextOfKin) || {}),
        },
        academicBackground: {
          primary: {
            ...emptySchool(),
            ...(academic.primary || {}),
            startDate: this.dateValue(academic.primary?.startDate),
            endDate: this.dateValue(academic.primary?.endDate),
          },
          secondary: {
            ...emptySchool(),
            ...(academic.secondary || {}),
            startDate: this.dateValue(academic.secondary?.startDate),
            endDate: this.dateValue(academic.secondary?.endDate),
          },
        },
        oLevelResultStatus: application.oLevelResultStatus || "awaiting_result",
        examinations: clone(application.examinations) || [],
        jamb: {
          registrationNumber: application.jamb?.registrationNumber || "",
          score: application.jamb?.score ?? "",
        },
      };
    },
    async fetchCountries() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/applications/reference/countries`
        );
        this.countries = (response.data?.data?.countries || [])
          .map((item) => item.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
      } catch {
        this.countries = [];
      }
    },
    async fetchStates() {
      try {
        const response = await fetch(
          "https://nga-states-lga.onrender.com/fetch",
          {
            credentials: "omit",
          }
        );
        if (!response.ok) throw new Error("Unable to load states");
        this.states = await response.json();
        await this.fetchLgas();
      } catch {
        this.states = [];
      }
    },
    async fetchLgas() {
      if (!this.form.stateOfOrigin) {
        this.lgas = [];
        return;
      }
      try {
        const response = await fetch(
          `https://nga-states-lga.onrender.com/?state=${encodeURIComponent(
            this.form.stateOfOrigin
          )}`,
          { credentials: "omit" }
        );
        if (!response.ok) throw new Error("Unable to load LGAs");
        this.lgas = await response.json();
      } catch {
        this.lgas = [];
      }
    },
    setOLevelStatus() {
      if (this.form.oLevelResultStatus === "awaiting_result") {
        this.form.examinations = [];
      } else if (!this.form.examinations.length) {
        this.addSitting();
      }
    },
    addSitting() {
      if (this.form.examinations.length >= 2) return;
      this.form.examinations.push({
        examType: "",
        examYear: "",
        examNumber: "",
        subjects: [{ name: "", grade: "" }],
      });
    },
    addSubject(exam) {
      if (exam.subjects.length < 11)
        exam.subjects.push({ name: "", grade: "" });
    },
    async save() {
      this.error = "";
      if (this.reason.trim().length < 5) {
        this.error = "Enter a correction reason of at least 5 characters.";
        return;
      }
      this.saving = true;
      try {
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/applications/${this.application._id}/details`,
          {
            expectedVersion: this.application.version,
            reason: this.reason.trim(),
            changes: this.form,
          }
        );
        this.$emit("saved");
      } catch (error) {
        this.error =
          error.response?.data?.message || "Unable to correct the application.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div class="action-backdrop" role="dialog" aria-modal="true">
    <div class="action-dialog bg-white rounded shadow-lg">
      <header
        class="d-flex justify-content-between align-items-start border-bottom p-3"
      >
        <div>
          <h5 class="fw-bold mb-1">Edit application</h5>
          <small class="text-muted"
            >{{ application.applicationNumber }} · application-form information
            only</small
          >
        </div>
        <button
          class="btn-close"
          type="button"
          :disabled="saving"
          @click="$emit('close')"
        ></button>
      </header>

      <div class="action-body p-3">
        <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>
        <ul class="nav nav-pills gap-1 mb-3">
          <li
            v-for="tab in [
              ['personal', 'Personal'],
              ['contacts', 'Contacts'],
              ['academic', 'Academic'],
              ['results', 'Results'],
            ]"
            :key="tab[0]"
            class="nav-item"
          >
            <button
              class="nav-link"
              :class="{ active: activeTab === tab[0] }"
              type="button"
              @click="activeTab = tab[0]"
            >
              {{ tab[1] }}
            </button>
          </li>
        </ul>

        <div v-if="activeTab === 'personal'" class="row g-3">
          <div class="col-md-4">
            <label class="form-label">Gender</label
            ><select v-model="form.gender" class="form-select">
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Marital status</label
            ><select v-model="form.maritalStatus" class="form-select">
              <option value="">Select</option>
              <option
                v-for="item in ['single', 'married', 'divorced', 'widowed']"
                :key="item"
                :value="item"
                class="text-capitalize"
              >
                {{ item }}
              </option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Nationality</label
            ><select v-model="form.nationality" class="form-select">
              <option value="">Select</option>
              <option v-for="country in countries" :key="country">
                {{ country }}
              </option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Place of birth</label
            ><select
              v-if="form.nationality === 'Nigeria' && states.length"
              v-model="form.placeOfBirth"
              class="form-select"
            >
              <option value="">Select state</option>
              <option v-for="state in states" :key="state">{{ state }}</option>
            </select>
            <input
              v-else
              v-model.trim="form.placeOfBirth"
              class="form-control"
            />
          </div>
          <div class="col-md-4">
            <label class="form-label">State of origin</label
            ><select
              v-if="form.nationality === 'Nigeria' && states.length"
              v-model="form.stateOfOrigin"
              class="form-select"
              @change="fetchLgas"
            >
              <option value="">Select state</option>
              <option v-for="state in states" :key="state">{{ state }}</option>
            </select>
            <input
              v-else
              v-model.trim="form.stateOfOrigin"
              class="form-control"
            />
          </div>
          <div class="col-md-4">
            <label class="form-label">Local government area</label
            ><select
              v-if="form.nationality === 'Nigeria' && lgas.length"
              v-model="form.lga"
              class="form-select"
            >
              <option value="">Select LGA</option>
              <option v-for="lga in lgas" :key="lga">{{ lga }}</option>
            </select>
            <input v-else v-model.trim="form.lga" class="form-control" />
          </div>
          <div class="col-12">
            <label class="form-label">Address line 1</label
            ><input
              v-model.trim="form.permanentAddress.line1"
              class="form-control"
            />
          </div>
          <div class="col-12">
            <label class="form-label">Address line 2</label
            ><input
              v-model.trim="form.permanentAddress.line2"
              class="form-control"
            />
          </div>
          <div class="col-md-4">
            <label class="form-label">City</label
            ><input
              v-model.trim="form.permanentAddress.city"
              class="form-control"
            />
          </div>
          <div class="col-md-4">
            <label class="form-label">State</label
            ><input
              v-model.trim="form.permanentAddress.state"
              class="form-control"
            />
          </div>
          <div class="col-md-4">
            <label class="form-label">Country</label
            ><select
              v-model="form.permanentAddress.country"
              class="form-select"
            >
              <option value="">Select</option>
              <option v-for="country in countries" :key="country">
                {{ country }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="activeTab === 'contacts'">
          <div
            v-for="contact in [
              ['guardian', 'Guardian'],
              ['nextOfKin', 'Next of kin'],
            ]"
            :key="contact[0]"
            class="border rounded p-3 mb-3"
          >
            <h6 class="fw-bold">{{ contact[1] }}</h6>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Name</label
                ><input
                  v-model.trim="form[contact[0]].name"
                  class="form-control"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Relationship</label
                ><select
                  v-model="form[contact[0]].relationship"
                  class="form-select"
                >
                  <option value="">Select</option>
                  <option
                    v-for="relationship in relationshipOptions"
                    :key="relationship"
                  >
                    {{ relationship }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Phone</label
                ><input
                  v-model.trim="form[contact[0]].phone"
                  class="form-control"
                />
              </div>
              <div class="col-12">
                <label class="form-label">Address</label
                ><textarea
                  v-model.trim="form[contact[0]].address"
                  class="form-control"
                  rows="2"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'academic'">
          <div
            v-for="school in [
              ['primary', 'Primary school'],
              ['secondary', 'Secondary school'],
            ]"
            :key="school[0]"
            class="border rounded p-3 mb-3"
          >
            <h6 class="fw-bold">{{ school[1] }}</h6>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">School name</label
                ><input
                  v-model.trim="form.academicBackground[school[0]].name"
                  class="form-control"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Start date</label
                ><input
                  v-model="form.academicBackground[school[0]].startDate"
                  type="date"
                  class="form-control"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">End date</label
                ><input
                  v-model="form.academicBackground[school[0]].endDate"
                  type="date"
                  class="form-control"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'results'">
          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <label class="form-label">O-Level status</label
              ><select
                v-model="form.oLevelResultStatus"
                class="form-select"
                @change="setOLevelStatus"
              >
                <option value="awaiting_result">Awaiting result</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div class="col-md-5">
              <label class="form-label">JAMB registration number</label
              ><input
                v-model.trim="form.jamb.registrationNumber"
                class="form-control"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label">JAMB score</label
              ><input
                v-model.number="form.jamb.score"
                type="number"
                min="0"
                max="400"
                class="form-control"
              />
            </div>
          </div>
          <template v-if="form.oLevelResultStatus === 'completed'">
            <div
              v-for="(exam, examIndex) in form.examinations"
              :key="examIndex"
              class="border rounded p-3 mb-3"
            >
              <div class="d-flex justify-content-between">
                <h6 class="fw-bold">Sitting {{ examIndex + 1 }}</h6>
                <button
                  v-if="form.examinations.length > 1"
                  class="btn btn-sm btn-outline-danger"
                  type="button"
                  @click="form.examinations.splice(examIndex, 1)"
                >
                  Remove
                </button>
              </div>
              <div class="row g-2 mb-3">
                <div class="col-md-4">
                  <select v-model="exam.examType" class="form-select">
                    <option value="">Exam type</option>
                    <option v-for="type in examTypes" :key="type">
                      {{ type }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4">
                  <input
                    v-model.number="exam.examYear"
                    type="number"
                    class="form-control"
                    placeholder="Year"
                  />
                </div>
                <div class="col-md-4">
                  <input
                    v-model.trim="exam.examNumber"
                    class="form-control"
                    placeholder="Exam number"
                  />
                </div>
              </div>
              <div
                v-for="(subject, subjectIndex) in exam.subjects"
                :key="subjectIndex"
                class="row g-2 mb-2"
              >
                <div class="col-7">
                  <select v-model="subject.name" class="form-select">
                    <option value="">Subject</option>
                    <option v-for="name in subjectOptions" :key="name">
                      {{ name }}
                    </option>
                  </select>
                </div>
                <div class="col-3">
                  <select v-model="subject.grade" class="form-select">
                    <option value="">Grade</option>
                    <option v-for="grade in grades" :key="grade">
                      {{ grade }}
                    </option>
                  </select>
                </div>
                <div class="col-2">
                  <button
                    class="btn btn-outline-danger w-100"
                    type="button"
                    :disabled="exam.subjects.length === 1"
                    @click="exam.subjects.splice(subjectIndex, 1)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
              <button
                class="btn btn-sm btn-outline-primary"
                type="button"
                @click="addSubject(exam)"
              >
                Add subject
              </button>
            </div>
            <button
              class="btn btn-sm btn-outline-primary"
              type="button"
              :disabled="form.examinations.length >= 2"
              @click="addSitting"
            >
              Add sitting
            </button>
          </template>
        </div>

        <div class="mt-4">
          <label class="form-label fw-semibold">Reason for correction *</label
          ><textarea
            v-model.trim="reason"
            class="form-control"
            rows="3"
            maxlength="1000"
            placeholder="This is included in the audit history and applicant notification."
          ></textarea>
        </div>
      </div>

      <footer class="border-top p-3 d-flex justify-content-end gap-2">
        <button
          class="btn btn-light"
          type="button"
          :disabled="saving"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="saving"
          @click="save"
        >
          <span
            v-if="saving"
            class="spinner-border spinner-border-sm me-1"
          ></span
          >Save corrections
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.action-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(0, 0, 0, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.action-dialog {
  width: min(980px, 100%);
  max-height: 94vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.action-body {
  overflow-y: auto;
}
@media (max-width: 767.98px) {
  .action-backdrop {
    align-items: flex-start;
    overflow-y: auto;
  }
  .action-dialog {
    margin-block: 0.5rem;
    max-height: none;
  }
  .action-body {
    overflow: visible;
  }
}
</style>
